import React from 'react';
import PropTypes from 'prop-types';
import { FormattedNumber, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BULGARIAN_VENUE_IDS, EUR_TO_BGN_CONVERSION_RATE } from '../../appConstants';
import { getVenueId } from '../../selectors/root';
import { isCustomCurrencyCode, normalizeParts } from '../../utils/currencyFormatter';
import CurrencySymbol from '../CurrencySymbol';

export const PRICE_CONTEXT_PAYMENT_CTA = 'payment_cta';
export const PRICE_CONTEXT_MENU_ITEM = 'menu_item';
export const PRICE_CONTEXT_PRODUCT_DETAILS = 'product_details';
export const PRICE_CONTEXT_CHECKOUT_MODAL_SUBTOTAL = 'checkout_modal_subtotal';

const FormattedPrice = ({ value, showCurrency, context, intl }, { currency }) => {
  const venueId = useSelector(getVenueId);

  const options = {
    // Indonesian rupiah should be displayed without the decimal places.
    // Sen are obsolete subunits (no longer produced) and displaying prices with
    // .00 decimals looks strange in Indonesia.
    ...(currency === 'IDR' ? { maximumFractionDigits: 0 } : {}),
  };

  const numericValue = value / 100;

  if (!showCurrency) {
    // eslint-disable-next-line react/style-prop-object
    return <FormattedNumber style="decimal" value={numericValue} {...options} />;
  }

  if (currency === 'BGN' || (currency === 'EUR' && BULGARIAN_VENUE_IDS.includes(venueId))) {
    const alternateCurrency = currency === 'EUR' ? 'BGN' : 'EUR';
    const alternateValue = Math.round(
      currency === 'EUR' ? value * EUR_TO_BGN_CONVERSION_RATE : value / EUR_TO_BGN_CONVERSION_RATE
    );

    const showConversionRate = [
      PRICE_CONTEXT_PAYMENT_CTA,
      PRICE_CONTEXT_MENU_ITEM,
      PRICE_CONTEXT_PRODUCT_DETAILS,
      PRICE_CONTEXT_CHECKOUT_MODAL_SUBTOTAL,
    ].includes(context);

    return (
      <span>
        {/* eslint-disable-next-line react/style-prop-object */}
        <FormattedNumber style="currency" currency={currency} value={value / 100} {...options} /> /{' '}
        <FormattedNumber
          /* eslint-disable-next-line react/style-prop-object */
          style="currency"
          currency={alternateCurrency}
          value={alternateValue / 100}
          {...options}
        />
        {showConversionRate && <> (€1 = BGN {EUR_TO_BGN_CONVERSION_RATE})</>}
      </span>
    );
  }

  if (isCustomCurrencyCode(currency)) {
    const parts = intl.formatNumberToParts(numericValue, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      ...options,
    });

    const normalizedParts = normalizeParts(parts);

    return (
      <span>
        {normalizedParts.map((part, index) => {
          const key = `${part.type}-${part.value}-${index}`;

          if (part.type === 'currency') {
            return <CurrencySymbol key={key} currencyCode={currency} />;
          }

          return <span key={key}>{part.value}</span>;
        })}
      </span>
    );
  }

  // eslint-disable-next-line react/style-prop-object
  return <FormattedNumber style="currency" currency={currency} value={numericValue} {...options} />;
};

FormattedPrice.defaultProps = {
  showCurrency: true,
  context: undefined,
};

FormattedPrice.propTypes = {
  value: PropTypes.number,
  showCurrency: PropTypes.bool,
  context: PropTypes.string,
  intl: PropTypes.object.isRequired,
};

FormattedPrice.contextTypes = {
  currency: PropTypes.string,
};

export default injectIntl(FormattedPrice);
